import { html, render, nothing } from "lit-html";
import { api, ApiError } from "./api.js";
import { App, type Page, type View } from "./app.js";
import { icons } from "./icons.js";
import { overview } from "./views/overview.js";
import { divisions } from "./views/divisions.js";
import { reps } from "./views/reps.js";
import { mapview } from "./views/mapview.js";
import { opps } from "./views/opps.js";
import { explore } from "./views/explore.js";
import { board } from "./views/board.js";
import { customer } from "./views/customer.js";
import { account, users } from "./views/account.js";

const root = document.getElementById("app")!;

try {
  const saved = localStorage.getItem("ffd-theme");
  if (saved === "light" || saved === "dark") document.documentElement.dataset.theme = saved;
} catch { /* storage blocked */ }

const views: Record<Page, View<any>> = { overview, divisions, reps, map: mapview, opportunities: opps, explore, board, customer, account, users };

let pending: { username: string; password: string } | null = null;
let trustDays = 30;

function loginScreen(error: string | null = null, busy = false) {
  const submit = async (e: Event) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const fd = new FormData(form);
    const creds = pending ?? { username: String(fd.get("username") ?? ""), password: String(fd.get("password") ?? "") };
    const code = pending ? String(fd.get("code") ?? "").trim() : undefined;
    const trust = pending ? fd.get("trust") === "on" : undefined;
    loginScreen(null, true);
    try {
      const r = await api.login(creds.username, creds.password, code, trust);
      if (r.needCode) { pending = creds; trustDays = r.trustDays ?? trustDays; loginScreen(null); return; }
      pending = null;
      await boot();
    } catch (err) {
      if (err instanceof ApiError && err.data?.needCode) { pending = creds; loginScreen(err.message); return; }
      pending = null;
      loginScreen(err instanceof ApiError ? err.message : "Couldn't sign in.");
    }
  };
  if (pending) {
    render(html`<div class="backdrop"></div>
      <div class="login">
        <form class="glass login-card" @submit=${submit}>
          <div class="brand"><div class="brand-mark">${icons.flame}</div><div><div class="brand-name">Feld Fire</div><div class="brand-sub">Sales Dashboard</div></div></div>
          <h1>Enter your code</h1>
          <p>Open your authenticator app and type the 6-digit code for Feld Fire Dashboard. Lost your phone? Use one of your recovery codes.</p>
          <div class="field"><label for="c">Code</label><input id="c" class="input" name="code" inputmode="numeric" autocomplete="one-time-code" autocapitalize="none" spellcheck="false" required ?disabled=${busy}></div>
          <label class="check"><input type="checkbox" name="trust" checked ?disabled=${busy}><span>Trust this device for ${trustDays} days (don't ask for a code here)</span></label>
          ${error ? html`<div class="err-text" role="alert">${error}</div>` : nothing}
          <button class="btn primary" style="height:42px;border-radius:13px" type="submit" ?disabled=${busy}>${busy ? "Checking…" : "Sign in"}</button>
          <button class="link" type="button" @click=${() => { pending = null; loginScreen(); }}>Use a different account</button>
        </form>
      </div>`, root);
    if (!busy) (root.querySelector("#c") as HTMLInputElement | null)?.focus();
    return;
  }
  render(html`<div class="backdrop"></div>
    <div class="login">
      <form class="glass login-card" @submit=${submit}>
        <div class="brand"><div class="brand-mark">${icons.flame}</div><div><div class="brand-name">Feld Fire</div><div class="brand-sub">Sales Dashboard</div></div></div>
        <h1>Sign in</h1>
        <p>Managers and shareholders only.</p>
        <div class="field"><label for="u">Username</label><input id="u" class="input" name="username" autocomplete="username" autocapitalize="none" spellcheck="false" required ?disabled=${busy}></div>
        <div class="field"><label for="p">Password</label><input id="p" class="input" name="password" type="password" autocomplete="current-password" required ?disabled=${busy}></div>
        ${error ? html`<div class="err-text" role="alert">${error}</div>` : nothing}
        <button class="btn primary" style="height:42px;border-radius:13px" type="submit" ?disabled=${busy}>${busy ? "Signing in…" : "Sign in"}</button>
      </form>
    </div>`, root);
  if (!busy) (root.querySelector("#u") as HTMLInputElement | null)?.focus();
}

async function boot() {
  try {
    const me = await api.me();
    const app = new App(root, views);
    await app.start(me);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) loginScreen();
    else render(html`<div class="backdrop"></div><div class="login"><div class="glass login-card"><h1>Can't load the dashboard</h1><p>${(e as Error).message}</p><button class="btn primary" @click=${() => location.reload()}>Try again</button></div></div>`, root);
  }
}

void boot();
